import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shuffle, Sparkles, TrendingUp, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const SHUFFLE_INTERVAL = 15000; // 15 seconds
const DISPLAY_COUNT = 8; // Cards to show at once

/**
 * RotatingSpotlight - Fair exposure component for marketplace
 * 
 * Features:
 * - Fetches weighted random listings (small sellers prioritized)
 * - Auto-shuffles display every 15 seconds with fade animation
 * - Local shuffle to avoid constant DB refetches
 * - Manual shuffle button for user control
 */
export const RotatingSpotlight = () => {
  const { api, isAuthenticated } = useAuth();
  const [allListings, setAllListings] = useState([]);
  const [displayListings, setDisplayListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [shuffleCount, setShuffleCount] = useState(0);
  const intervalRef = useRef(null);

  // Fetch spotlight listings once
  const fetchSpotlight = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api().get('/marketplace/spotlight?count=32');
      const listings = response.data.spotlight || [];
      setAllListings(listings);
      
      // Initial display
      shuffleDisplay(listings);
    } catch (error) {
      console.error('Failed to fetch spotlight:', error);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  // Local shuffle from cached listings
  const shuffleDisplay = useCallback((listings = allListings) => {
    if (listings.length === 0) return;
    
    // Fade out
    setIsFading(true);
    
    setTimeout(() => {
      // Fisher-Yates shuffle
      const shuffled = [...listings];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      
      setDisplayListings(shuffled.slice(0, DISPLAY_COUNT));
      setShuffleCount(prev => prev + 1);
      
      // Fade in
      setTimeout(() => setIsFading(false), 50);
    }, 300);
  }, [allListings]);

  // Manual shuffle handler
  const handleManualShuffle = () => {
    shuffleDisplay();
  };

  // Track listing click
  const trackClick = async (listingId) => {
    try {
      await api().post('/marketplace/track', {
        listing_id: listingId,
        interaction_type: 'click'
      });
    } catch (error) {
      // Silent fail - tracking shouldn't block UX
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSpotlight();
  }, [fetchSpotlight]);

  // Auto-shuffle interval
  useEffect(() => {
    if (allListings.length > DISPLAY_COUNT) {
      intervalRef.current = setInterval(() => {
        shuffleDisplay();
      }, SHUFFLE_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [allListings, shuffleDisplay]);

  if (isLoading) {
    return (
      <div className="py-12">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
          <h2 className="text-2xl font-bold text-white">Spotlight</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-zinc-800/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (displayListings.length === 0) {
    return null;
  }

  return (
    <div className="py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-400" />
          <h2 className="text-2xl font-bold text-white">Rotating Spotlight</h2>
          <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded-full">
            Fair exposure for all sellers
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualShuffle}
            className="text-zinc-400 hover:text-white"
            data-testid="shuffle-btn"
          >
            <Shuffle className="w-4 h-4 mr-2" />
            Shuffle
          </Button>
          <Link to="/marketplace">
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Listings Grid */}
      <div 
        className={`grid grid-cols-2 md:grid-cols-4 gap-4 transition-opacity duration-300 ${
          isFading ? 'opacity-0' : 'opacity-100'
        }`}
        data-testid="spotlight-grid"
      >
        {displayListings.map((listing) => (
          <Link
            key={listing.id}
            to={`/listing/${listing.id}`}
            onClick={() => trackClick(listing.id)}
            className="group"
            data-testid={`spotlight-card-${listing.id}`}
          >
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden hover:border-amber-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/10">
              {/* Image */}
              <div className="aspect-[3/4] relative overflow-hidden bg-zinc-800">
                <img
                  src={listing.images?.[0] || listing.image_url || '/placeholder-card.png'}
                  alt={listing.player_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {/* Grading Badge */}
                {listing.grading_company && listing.grade && (
                  <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/70 text-xs font-bold text-white">
                    {listing.grading_company} {listing.grade}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="font-semibold text-white text-sm truncate group-hover:text-amber-400 transition-colors">
                  {listing.player_name}
                </h3>
                <p className="text-xs text-zinc-500 truncate mt-1">
                  {listing.year} {listing.set_name}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-emerald-400 font-bold">
                    ${listing.price?.toLocaleString()}
                  </span>
                  <span className="text-xs text-zinc-600">
                    @{listing.seller_username}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Shuffle indicator */}
      <div className="flex items-center justify-center mt-6 gap-2 text-xs text-zinc-600">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
        <span>Auto-refreshes every 15 seconds</span>
      </div>
    </div>
  );
};

export default RotatingSpotlight;
